import React from 'react';
import { Stack, Unstable_Grid2 as Grid, Typography, Box } from '@mui/material';

import { Checkboxes, ToggleButtonGroupField, Select } from '@occc-app/design-system';
import {
  eaivAttributes,
  verifiedItems,
} from '@occc-app/routes/Audience/components/FuelAndConvenienceProfile/constants';
import { LocationMultipleSelectField } from '@occc-app/routes/Location/components/LocationSelect/LocationMultipleSelectField';
import { ProductCategorySelectField } from '@occc-app/routes/Audience/components/ProductInterests/ProductCategorySelectField';
import {
  matchItems,
  preferItems,
} from '@occc-app/routes/Audience/components/ProductInterests/constants';
import { LastVisitTargetingAttribute } from '@occc-app/routes/Audience/components/FuelAndConvenienceProfile/LastVisitTargetingAttribute';

import { useConfig } from '@occc/config';
import { ActiveSubbrandAttributeAttribute } from '@occc-app/routes/Audience/components/FuelAndConvenienceProfile/ActiveSubbrandAttribute';
import { Field } from 'react-final-form';
import { LocationGroupMultipleSelect } from '@occc/feat-constraints';
import { AcquisitionChannelField } from './AcquisitionChannel';
import { PointBalanceTargetingAttribute } from './PointBalanceTargetingAttribute';
import { MembershipCreatedTargetingAttribute } from './MembershipCreatedTargetingAttribute';
import { EmployeeStatusTargetingAttribute } from './EmployeeStatusTargetingAttribute';
import { HasACHTargetingAttribute } from './HasACHTargetingAttribute';
import { CommunicationOptInTargetingAttribute } from './CommunicationOptInTargetingAttribute';
import { AgeWithinFormContent } from '../AgeWithin/AgeWithinFormContent';
import { ExternalIdentityVerificationContent } from '../ExternalIdentityVerification/ExternalIdentityVerificationContent';

import { AudienceFormValuesPaths, AudienceFormValues } from '../../types';

type MemberAttributesFormContentProps = {
  name: string;
  productInterestsFieldName: string;
  lastVisitFieldName: string;
  pointBalanceFieldName: string;
  membershipCreatedFieldName: string;
  clearFormField: (fieldName: string) => void;
};

export const MemberAttributesFormContent = React.memo(
  ({
    name,
    productInterestsFieldName,
    lastVisitFieldName,
    pointBalanceFieldName,
    membershipCreatedFieldName,
    clearFormField,
  }: MemberAttributesFormContentProps) => {
    const { config } = useConfig();

    return (
      <Stack spacing={4} mb={1}>
        {config.externalIdentityVerification?.enabled && <ExternalIdentityVerificationContent />}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          <Box flex={1}>
            <Field<AudienceFormValues['ageWithin']> name='ageWithin'>
              {({ input }) => (
                <AgeWithinFormContent
                  name='ageWithin'
                  values={input.value ?? {}}
                  clearFormField={clearFormField}
                />
              )}
            </Field>
          </Box>
          <Box flex={2}>
            <Typography paragraph variant='subtitle1'>
              Home Store
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <Box flex={1}>
                <LocationMultipleSelectField
                  inputProps={{ margin: 'none' }}
                  name={'homeStore.locations' satisfies AudienceFormValuesPaths}
                  label='Locations'
                />
              </Box>
              <Box flex={1}>
                <LocationGroupMultipleSelect
                  inputProps={{ margin: 'none' }}
                  name={'homeStore.locationGroups' satisfies AudienceFormValuesPaths}
                  label='Location Groups'
                />
              </Box>
            </Stack>
          </Box>
        </Stack>

        <div>
          <Typography paragraph variant='subtitle1'>
            Product Interests
          </Typography>
          <Grid container spacing={3}>
            <Grid md='auto'>
              <ToggleButtonGroupField
                name={`${productInterestsFieldName}.prefer`}
                buttons={preferItems}
              />
            </Grid>

            <Grid md='auto'>
              <Select name={`${productInterestsFieldName}.match`} data={matchItems} />
            </Grid>

            <Grid sm={12} md>
              <ProductCategorySelectField
                name={`${productInterestsFieldName}.productCategories`}
                inputProps={{ margin: 'none' }}
              />
            </Grid>
          </Grid>
        </div>

        <div>
          <Typography paragraph variant='subtitle1'>
            EAIV Verified
          </Typography>
          <Grid container spacing={3}>
            <Grid sm={12} md='auto'>
              <ToggleButtonGroupField name={`${name}.eaivVerified`} buttons={verifiedItems} />
            </Grid>

            <Grid sm={12} md='auto' sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkboxes
                name={`${name}.eaivAttributes`}
                data={eaivAttributes}
                formGroupProps={{ sx: { display: 'flex', flexFlow: 'row' } }}
                formControlLabelProps={{ sx: { mr: 3 } }}
              />
            </Grid>
          </Grid>
        </div>

        <div>
          <Typography paragraph variant='subtitle1'>
            Acquisition Channel
          </Typography>
          <AcquisitionChannelField name={`${name}.acquisitionChannelAttribute`} />
        </div>

        <div>
          <Typography paragraph variant='subtitle1'>
            Member last visited a location
          </Typography>
          <Field<AudienceFormValues['lastVisit']> name={lastVisitFieldName}>
            {({ input }) => (
              <LastVisitTargetingAttribute
                lastVisitFieldName={lastVisitFieldName}
                lastVisitAmount={input.value?.amount}
              />
            )}
          </Field>
        </div>

        {config.subBrands?.enabled && config.subBrands.list.length > 0 && (
          <div>
            <Typography paragraph variant='subtitle1'>
              Active Subbrand
            </Typography>
            <ActiveSubbrandAttributeAttribute fieldName={name} />
          </div>
        )}

        <div>
          <Typography paragraph variant='subtitle1'>
            Membership was created
          </Typography>
          <MembershipCreatedTargetingAttribute fieldName={membershipCreatedFieldName} />
        </div>

        <div>
          <Typography paragraph variant='subtitle1'>
            Points balance
          </Typography>
          <Field<AudienceFormValues['pointBalance']> name={pointBalanceFieldName}>
            {({ input }) => (
              <PointBalanceTargetingAttribute
                pointBalanceFieldName={pointBalanceFieldName}
                operator={input.value?.operator ?? ''}
              />
            )}
          </Field>
        </div>

        <div style={{ marginTop: 0 }}>
          <Typography paragraph variant='subtitle1' style={{ marginBottom: 0 }}>
            Employee Status
          </Typography>
          <EmployeeStatusTargetingAttribute name='employeeTargetingAttributes' />
        </div>

        {config.ach.enabled && (
          <div>
            <Typography paragraph variant='subtitle1'>
              Enrolled in ACH
            </Typography>
            <HasACHTargetingAttribute formFieldName={name} />
          </div>
        )}

        <div>
          <Typography paragraph variant='subtitle1'>
            SMS Opt In Status
          </Typography>
          <CommunicationOptInTargetingAttribute fieldName='communicationPreferences.sms' />
        </div>

        <div>
          <Typography paragraph variant='subtitle1'>
            Push Opt In Status
          </Typography>
          <CommunicationOptInTargetingAttribute fieldName='communicationPreferences.push' />
        </div>
      </Stack>
    );
  },
);
